import React, { useState, useCallback } from "react";
import { Trans, t } from "@lingui/macro";

import "./TpSlModal.css";

import Modal from "components/Modal/Modal";
import { createDecreaseOrder, createDecreaseOrderWithTrail } from "domain/legacy";
import { formatAmount } from "lib/numbers";
import { getPriceDecimals } from "config/tokens";
import { helperToast } from "lib/helperToast";
import { USD_DECIMALS } from "config/factors";

const PRICE_PRECISION = 10n ** 30n;

type Position = {
  key: string;
  indexToken: { symbol: string; address: string };
  collateralToken: { address: string };
  isLong: boolean;
  size: bigint;
  markPrice: bigint;
  averagePrice: bigint;
  leverage?: bigint;    // GMX basis-points leverage (1x = 10000, 10x = 100000)
  collateral?: bigint;  // fallback to derive leverage from size/collateral
};

// Convert a target ROI% (TP: gain, SL: loss) into a trigger price using the
// position's entry price and leverage. ROI% = leverage × price-move%, so the
// required price move% = ROI% / leverage. This lets users set TP/SL by percent
// instead of looking up an exact price.
function pctToPrice(position: Position, percentStr: string, isTp: boolean, priceDecimals: number): string {
  const pct = parseFloat(percentStr);
  if (isNaN(pct) || pct <= 0) return "";
  let leverageX = position.leverage ? Number(position.leverage) / 10000 : 0;
  if (!(leverageX > 0) && position.collateral && position.collateral > 0n) {
    leverageX = Number(position.size) / Number(position.collateral);
  }
  if (!(leverageX > 0)) leverageX = 1;
  const entry = Number(formatAmount(position.averagePrice, USD_DECIMALS, priceDecimals, false));
  if (!(entry > 0)) return "";
  const frac = pct / 100 / leverageX;
  const price = isTp
    ? position.isLong ? entry * (1 + frac) : entry * (1 - frac)
    : position.isLong ? entry * (1 - frac) : entry * (1 + frac);
  return price > 0 ? price.toFixed(priceDecimals) : "";
}

type Props = {
  isVisible: boolean;
  position: Position | null;
  chainId: number;
  signer: any;
  pendingTxns: any[];
  setPendingTxns: (txns: any[]) => void;
  onClose: () => void;
  orderBookApproved?: boolean;
  approveOrderBook?: () => any;
};

export default function TpSlModal({ isVisible, position, chainId, signer, pendingTxns, setPendingTxns, onClose, orderBookApproved, approveOrderBook }: Props) {
  const [tpPrice, setTpPrice] = useState("");
  const [slPrice, setSlPrice] = useState("");
  const [tpPercent, setTpPercent] = useState("");
  const [slPercent, setSlPercent] = useState("");
  const [inputMode, setInputMode] = useState<"percent" | "price">("percent");
  const [useTrailingStop, setUseTrailingStop] = useState(false);
  const [trailDistance, setTrailDistance] = useState(""); // USD distance
  const [isSubmitting, setIsSubmitting] = useState(false);

  const priceDecimals = position ? getPriceDecimals(chainId, position.indexToken.symbol) : 2;
  const markPriceFormatted = position
    ? formatAmount(position.markPrice, USD_DECIMALS, priceDecimals, true)
    : "";

  // Effective trigger prices actually submitted: derived from the % inputs when
  // in percent mode, otherwise the directly-typed prices.
  const effTpPrice = position && inputMode === "percent" ? pctToPrice(position, tpPercent, true, priceDecimals) : tpPrice;
  const effSlPrice = position && inputMode === "percent" ? pctToPrice(position, slPercent, false, priceDecimals) : slPrice;

  const validate = useCallback(() => {
    if (!position) return t`No position`;

    const mark = Number(formatAmount(position.markPrice, USD_DECIMALS, priceDecimals, false));

    if (useTrailingStop) {
      const dist = parseFloat(trailDistance);
      if (isNaN(dist) || dist <= 0) return t`Enter trail distance`;
      return null;
    }

    if (!effTpPrice && !effSlPrice) {
      return inputMode === "percent" ? t`Enter a take-profit or stop-loss %` : t`Enter at least one price`;
    }

    if (effTpPrice) {
      const tp = parseFloat(effTpPrice);
      if (isNaN(tp) || tp <= 0) return t`Invalid TP price`;
      if (position.isLong && tp <= mark) return t`TP must be above mark price for long`;
      if (!position.isLong && tp >= mark) return t`TP must be below mark price for short`;
    }

    if (effSlPrice) {
      const sl = parseFloat(effSlPrice);
      if (isNaN(sl) || sl <= 0) return t`Invalid SL price`;
      if (position.isLong && sl >= mark) return t`SL must be below mark price for long`;
      if (!position.isLong && sl <= mark) return t`SL must be above mark price for short`;
    }

    return null;
  }, [position, effTpPrice, effSlPrice, inputMode, useTrailingStop, trailDistance, priceDecimals]);

  const handleSubmit = useCallback(async () => {
    if (!position || !signer) return;
    const error = validate();
    if (error) {
      helperToast.error(error);
      return;
    }

    // TP/SL orders execute through the OrderBook — require the one-time plugin
    // ("Enable Orders") approval, or the created orders can never execute.
    if (orderBookApproved === false) {
      if (approveOrderBook) approveOrderBook();
      helperToast.error(t`Enable Orders first, then set your TP/SL.`);
      return;
    }

    setIsSubmitting(true);
    const opts = { setPendingTxns, pendingTxns };

    // Convert USD price string to 1e30 bigint
    const priceToP30 = (s: string): bigint => {
      const num = parseFloat(s);
      if (isNaN(num) || num <= 0) return 0n;
      return BigInt(Math.round(num * 1e8)) * (10n ** 22n);
    };

    try {
      const promises: Promise<any>[] = [];

      if (useTrailingStop) {
        const trailAmountP30 = priceToP30(trailDistance);
        // Initial trigger price: mark price ± trail distance
        const mark = position.markPrice;
        const initialTrigger = position.isLong ? mark - trailAmountP30 : mark + trailAmountP30;
        promises.push(
          createDecreaseOrderWithTrail(
            chainId, signer,
            position.indexToken.address, position.size,
            position.collateralToken.address, 0n,
            position.isLong, initialTrigger,
            !position.isLong, // triggerAboveThreshold: false for long (stop below), true for short
            trailAmountP30, opts
          )
        );
      } else {
        if (effTpPrice) {
          promises.push(
            createDecreaseOrder(
              chainId, signer,
              position.indexToken.address, position.size,
              position.collateralToken.address, 0n,
              position.isLong, priceToP30(effTpPrice),
              position.isLong, // long TP triggers above
              opts
            )
          );
        }
        if (effSlPrice) {
          promises.push(
            createDecreaseOrder(
              chainId, signer,
              position.indexToken.address, position.size,
              position.collateralToken.address, 0n,
              position.isLong, priceToP30(effSlPrice),
              !position.isLong, // long SL triggers below
              opts
            )
          );
        }
      }

      await Promise.all(promises);
      helperToast.success(useTrailingStop ? t`Trailing stop submitted` : t`TP/SL orders submitted`);
      setTpPrice("");
      setSlPrice("");
      setTpPercent("");
      setSlPercent("");
      setTrailDistance("");
      onClose();
    } catch {
      // callContract shows error toast
    } finally {
      setIsSubmitting(false);
    }
  }, [position, signer, chainId, effTpPrice, effSlPrice, useTrailingStop, trailDistance, pendingTxns, setPendingTxns, validate, onClose]);

  if (!position) return null;

  const error = validate();

  return (
    <Modal
      isVisible={isVisible}
      setIsVisible={onClose}
      label={t`Set TP / SL — ${position.indexToken.symbol} ${position.isLong ? "Long" : "Short"}`}
      className="TpSlModal"
    >
      <div className="TpSlModal-content">
        <div className="TpSlModal-mark-row">
          <span className="muted">
            <Trans>Mark Price</Trans>
          </span>
          <span>${markPriceFormatted}</span>
        </div>

        <div className="TpSlModal-mode-toggle">
          <button
            className={`TpSlModal-mode-btn${!useTrailingStop ? " active" : ""}`}
            onClick={() => setUseTrailingStop(false)}
          >
            <Trans>TP / SL</Trans>
          </button>
          <button
            className={`TpSlModal-mode-btn${useTrailingStop ? " active" : ""}`}
            onClick={() => setUseTrailingStop(true)}
          >
            <Trans>Trailing Stop</Trans>
          </button>
        </div>

        {useTrailingStop ? (
          <div className="TpSlModal-field">
            <label className="TpSlModal-label">
              <span className="negative">
                <Trans>Trail Distance</Trans>
              </span>
              <span className="muted TpSlModal-hint">
                {position.isLong
                  ? t`stop trails ${trailDistance || "?"} below highest price`
                  : t`stop trails ${trailDistance || "?"} above lowest price`}
              </span>
            </label>
            <div className="TpSlModal-input-wrap">
              <span className="TpSlModal-prefix">$</span>
              <input
                className="TpSlModal-input"
                type="number"
                placeholder="e.g. 50"
                value={trailDistance}
                onChange={(e) => setTrailDistance(e.target.value)}
                step="any"
                min="0"
              />
            </div>
            <div className="TpSlModal-trail-info muted">
              <Trans>
                Keeper automatically adjusts the stop price as market moves in your favor.
                Order executes when price reverses by the trail distance.
              </Trans>
            </div>
          </div>
        ) : (
          <>
            {/* Enter TP/SL as a % of margin (default) or an exact price. */}
            <div className="TpSlModal-submode">
              <button
                className={`TpSlModal-submode-btn${inputMode === "percent" ? " active" : ""}`}
                onClick={() => setInputMode("percent")}
              >
                <Trans>By %</Trans>
              </button>
              <button
                className={`TpSlModal-submode-btn${inputMode === "price" ? " active" : ""}`}
                onClick={() => setInputMode("price")}
              >
                <Trans>By Price</Trans>
              </button>
            </div>

            <div className="TpSlModal-field">
              <label className="TpSlModal-label">
                <span className="positive">
                  <Trans>Take Profit</Trans>
                </span>
                <span className="muted TpSlModal-hint">
                  {inputMode === "percent" ? t`gain on margin` : position.isLong ? t`triggers above` : t`triggers below`}
                </span>
              </label>
              {inputMode === "percent" ? (
                <>
                  <div className="TpSlModal-input-wrap">
                    <input
                      className="TpSlModal-input"
                      type="number"
                      placeholder="e.g. 50"
                      value={tpPercent}
                      onChange={(e) => setTpPercent(e.target.value)}
                      step="any"
                      min="0"
                    />
                    <span className="TpSlModal-suffix">%</span>
                  </div>
                  <div className="TpSlModal-quickrow">
                    {[25, 50, 100, 200, 500].map((p) => (
                      <button
                        key={p}
                        className={`TpSlModal-quick${tpPercent === String(p) ? " active" : ""}`}
                        onClick={() => setTpPercent(String(p))}
                      >
                        +{p}%
                      </button>
                    ))}
                  </div>
                  {effTpPrice && (
                    <div className="TpSlModal-computed muted">
                      <Trans>Trigger price</Trans> ≈ ${effTpPrice}
                    </div>
                  )}
                </>
              ) : (
                <div className="TpSlModal-input-wrap">
                  <span className="TpSlModal-prefix">$</span>
                  <input
                    className="TpSlModal-input"
                    type="number"
                    placeholder={position.isLong ? t`Above ${markPriceFormatted}` : t`Below ${markPriceFormatted}`}
                    value={tpPrice}
                    onChange={(e) => setTpPrice(e.target.value)}
                    step="any"
                    min="0"
                  />
                </div>
              )}
            </div>

            <div className="TpSlModal-field">
              <label className="TpSlModal-label">
                <span className="negative">
                  <Trans>Stop Loss</Trans>
                </span>
                <span className="muted TpSlModal-hint">
                  {inputMode === "percent" ? t`loss on margin` : position.isLong ? t`triggers below` : t`triggers above`}
                </span>
              </label>
              {inputMode === "percent" ? (
                <>
                  <div className="TpSlModal-input-wrap">
                    <input
                      className="TpSlModal-input"
                      type="number"
                      placeholder="e.g. 25"
                      value={slPercent}
                      onChange={(e) => setSlPercent(e.target.value)}
                      step="any"
                      min="0"
                    />
                    <span className="TpSlModal-suffix">%</span>
                  </div>
                  <div className="TpSlModal-quickrow">
                    {[10, 25, 50, 75].map((p) => (
                      <button
                        key={p}
                        className={`TpSlModal-quick${slPercent === String(p) ? " active" : ""}`}
                        onClick={() => setSlPercent(String(p))}
                      >
                        -{p}%
                      </button>
                    ))}
                  </div>
                  {effSlPrice && (
                    <div className="TpSlModal-computed muted">
                      <Trans>Trigger price</Trans> ≈ ${effSlPrice}
                    </div>
                  )}
                </>
              ) : (
                <div className="TpSlModal-input-wrap">
                  <span className="TpSlModal-prefix">$</span>
                  <input
                    className="TpSlModal-input"
                    type="number"
                    placeholder={position.isLong ? t`Below ${markPriceFormatted}` : t`Above ${markPriceFormatted}`}
                    value={slPrice}
                    onChange={(e) => setSlPrice(e.target.value)}
                    step="any"
                    min="0"
                  />
                </div>
              )}
            </div>
          </>
        )}

        <div className="TpSlModal-info muted">
          <Trans>Orders will close the full position when triggered.</Trans>
        </div>

        {error && (
          <div className="TpSlModal-error negative">{error}</div>
        )}

        <button
          className="App-cta Exchange-swap-button TpSlModal-btn"
          disabled={isSubmitting || !!error}
          onClick={handleSubmit}
        >
          {isSubmitting ? (
            <Trans>Submitting...</Trans>
          ) : useTrailingStop ? (
            <Trans>Set Trailing Stop</Trans>
          ) : (
            <Trans>Confirm TP / SL</Trans>
          )}
        </button>
      </div>
    </Modal>
  );
}
